import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CreateDeliverable,
  CreateDeliverableMatch,
  Deliverable,
  DeliverableMatch,
  DeliverableSummary,
  UpdateDeliverable,
  UpdateDeliverableMatch,
} from '../types/deliverables-types';
import { baseQueryWithErrorHandling } from './baseQuery';

export const deliverablesApi = createApi({
  reducerPath: 'deliverablesApi',
  tagTypes: [
    'Deliverables',
    'Deliverable',
    'DeliverableMatches',
    'DeliverableMatch',
  ],
  baseQuery: baseQueryWithErrorHandling,
  endpoints: (builder) => ({
    // Task Deliverables - Primary pattern (by task)
    getTaskDeliverables: builder.query<
      Deliverable[],
      { matterId: string; taskId: string }
    >({
      query: ({ matterId, taskId }) =>
        `api/matters/${matterId}/tasks/${taskId}/deliverables/`,
      providesTags: (result, error, { matterId, taskId }) => [
        { type: 'Deliverables', id: `task-${taskId}` },
        ...(result || []).map(({ id }) => ({
          type: 'Deliverable' as const,
          id,
        })),
      ],
    }),

    // Create deliverable for a task
    createTaskDeliverable: builder.mutation<
      Deliverable,
      {
        matterId: string;
        taskId: string;
        deliverable: Omit<CreateDeliverable, 'task_id'>;
      }
    >({
      query: ({ matterId, taskId, deliverable }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/deliverables/`,
        method: 'POST',
        body: { ...deliverable, task_id: taskId },
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'Deliverables', id: `task-${taskId}` },
      ],
    }),

    // Get specific deliverable by task
    getTaskDeliverable: builder.query<
      Deliverable,
      { matterId: string; taskId: string; deliverableId: string }
    >({
      query: ({ matterId, taskId, deliverableId }) =>
        `api/matters/${matterId}/tasks/${taskId}/deliverables/${deliverableId}/`,
      providesTags: (result, error, { deliverableId }) => [
        { type: 'Deliverable', id: deliverableId },
      ],
    }),

    // Update deliverable
    updateTaskDeliverable: builder.mutation<
      Deliverable,
      {
        matterId: string;
        taskId: string;
        deliverableId: string;
        deliverable: UpdateDeliverable;
      }
    >({
      query: ({ matterId, taskId, deliverableId, deliverable }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/deliverables/${deliverableId}/`,
        method: 'PUT',
        body: deliverable,
      }),
      invalidatesTags: (result, error, { taskId, deliverableId }) => [
        { type: 'Deliverables', id: `task-${taskId}` },
        { type: 'Deliverable', id: deliverableId },
      ],
    }),

    // Partially update deliverable
    partialUpdateTaskDeliverable: builder.mutation<
      Deliverable,
      {
        matterId: string;
        taskId: string;
        deliverableId: string;
        deliverable: Partial<UpdateDeliverable>;
      }
    >({
      query: ({ matterId, taskId, deliverableId, deliverable }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/deliverables/${deliverableId}/`,
        method: 'PATCH',
        body: deliverable,
      }),
      invalidatesTags: (result, error, { taskId, deliverableId }) => [
        { type: 'Deliverables', id: `task-${taskId}` },
        { type: 'Deliverable', id: deliverableId },
      ],
    }),

    // Delete deliverable
    deleteTaskDeliverable: builder.mutation<
      void,
      { matterId: string; taskId: string; deliverableId: string }
    >({
      query: ({ matterId, taskId, deliverableId }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/deliverables/${deliverableId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { taskId, deliverableId }) => [
        { type: 'Deliverables', id: `task-${taskId}` },
        { type: 'Deliverable', id: deliverableId },
      ],
    }),

    // Bypass deliverable
    bypassDeliverable: builder.mutation<
      Deliverable,
      { matterId: string; taskId: string; deliverableId: string }
    >({
      query: ({ matterId, taskId, deliverableId }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/deliverables/${deliverableId}/bypass/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { taskId, deliverableId }) => [
        { type: 'Deliverables', id: `task-${taskId}` },
        { type: 'Deliverable', id: deliverableId },
      ],
    }),

    // Get deliverable summary for task
    getTaskDeliverableSummary: builder.query<
      DeliverableSummary,
      { matterId: string; taskId: string }
    >({
      query: ({ matterId, taskId }) =>
        `api/matters/${matterId}/tasks/${taskId}/deliverables/summary/`,
      providesTags: (result, error, { taskId }) => [
        { type: 'Deliverables', id: `task-${taskId}-summary` },
      ],
    }),

    // Matter Deliverables - Convenience pattern (all deliverables across tasks)
    getMatterDeliverables: builder.query<Deliverable[], { matterId: string }>({
      query: ({ matterId }) => `api/matters/${matterId}/deliverables/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'Deliverables', id: `matter-${matterId}` },
        ...(result || []).map(({ id }) => ({
          type: 'Deliverable' as const,
          id,
        })),
      ],
    }),

    // Get specific deliverable by matter
    getMatterDeliverable: builder.query<
      Deliverable,
      { matterId: string; deliverableId: string }
    >({
      query: ({ matterId, deliverableId }) =>
        `api/matters/${matterId}/deliverables/${deliverableId}/`,
      providesTags: (result, error, { deliverableId }) => [
        { type: 'Deliverable', id: deliverableId },
      ],
    }),

    // Batch create deliverables
    batchCreateDeliverables: builder.mutation<
      Deliverable[],
      { matterId: string; deliverables: CreateDeliverable[] }
    >({
      query: ({ matterId, deliverables }) => ({
        url: `api/matters/${matterId}/deliverables/batch_create/`,
        method: 'POST',
        body: deliverables,
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'Deliverables', id: `matter-${matterId}` },
      ],
    }),

    // Get pending deliverables for matter
    getPendingMatterDeliverables: builder.query<
      Deliverable[],
      { matterId: string }
    >({
      query: ({ matterId }) => `api/matters/${matterId}/deliverables/pending/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'Deliverables', id: `matter-${matterId}-pending` },
      ],
    }),

    // Get deliverable summary for matter
    getMatterDeliverableSummary: builder.query<
      DeliverableSummary,
      { matterId: string }
    >({
      query: ({ matterId }) => `api/matters/${matterId}/deliverables/summary/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'Deliverables', id: `matter-${matterId}-summary` },
      ],
    }),

    // Deliverable Matches
    getDeliverableMatches: builder.query<
      DeliverableMatch[],
      { matterId: string; taskId: string; deliverableId: string }
    >({
      query: ({ matterId, taskId, deliverableId }) =>
        `api/matters/${matterId}/tasks/${taskId}/deliverables/${deliverableId}/matches/`,
      providesTags: (result, error, { deliverableId }) => [
        { type: 'DeliverableMatches', id: deliverableId },
        ...(result || []).map(({ id }) => ({
          type: 'DeliverableMatch' as const,
          id,
        })),
      ],
    }),

    // Create deliverable match
    createDeliverableMatch: builder.mutation<
      DeliverableMatch,
      {
        matterId: string;
        taskId: string;
        deliverableId: string;
        match: CreateDeliverableMatch;
      }
    >({
      query: ({ matterId, taskId, deliverableId, match }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/deliverables/${deliverableId}/matches/`,
        method: 'POST',
        body: match,
      }),
      invalidatesTags: (result, error, { deliverableId }) => [
        { type: 'DeliverableMatches', id: deliverableId },
      ],
    }),

    // Get specific deliverable match
    getDeliverableMatch: builder.query<
      DeliverableMatch,
      {
        matterId: string;
        taskId: string;
        deliverableId: string;
        matchId: string;
      }
    >({
      query: ({ matterId, taskId, deliverableId, matchId }) =>
        `api/matters/${matterId}/tasks/${taskId}/deliverables/${deliverableId}/matches/${matchId}/`,
      providesTags: (result, error, { matchId }) => [
        { type: 'DeliverableMatch', id: matchId },
      ],
    }),

    // Update deliverable match
    updateDeliverableMatch: builder.mutation<
      DeliverableMatch,
      {
        matterId: string;
        taskId: string;
        deliverableId: string;
        matchId: string;
        match: UpdateDeliverableMatch;
      }
    >({
      query: ({ matterId, taskId, deliverableId, matchId, match }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/deliverables/${deliverableId}/matches/${matchId}/`,
        method: 'PUT',
        body: match,
      }),
      invalidatesTags: (result, error, { deliverableId, matchId }) => [
        { type: 'DeliverableMatches', id: deliverableId },
        { type: 'DeliverableMatch', id: matchId },
      ],
    }),

    // Partially update deliverable match
    partialUpdateDeliverableMatch: builder.mutation<
      DeliverableMatch,
      {
        matterId: string;
        taskId: string;
        deliverableId: string;
        matchId: string;
        match: Partial<UpdateDeliverableMatch>;
      }
    >({
      query: ({ matterId, taskId, deliverableId, matchId, match }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/deliverables/${deliverableId}/matches/${matchId}/`,
        method: 'PATCH',
        body: match,
      }),
      invalidatesTags: (result, error, { deliverableId, matchId }) => [
        { type: 'DeliverableMatches', id: deliverableId },
        { type: 'DeliverableMatch', id: matchId },
      ],
    }),

    // Delete deliverable match
    deleteDeliverableMatch: builder.mutation<
      void,
      {
        matterId: string;
        taskId: string;
        deliverableId: string;
        matchId: string;
      }
    >({
      query: ({ matterId, taskId, deliverableId, matchId }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/deliverables/${deliverableId}/matches/${matchId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { deliverableId, matchId }) => [
        { type: 'DeliverableMatches', id: deliverableId },
        { type: 'DeliverableMatch', id: matchId },
      ],
    }),

    // Approve deliverable match
    approveDeliverableMatch: builder.mutation<
      DeliverableMatch,
      {
        matterId: string;
        taskId: string;
        deliverableId: string;
        matchId: string;
      }
    >({
      query: ({ matterId, taskId, deliverableId, matchId }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/deliverables/${deliverableId}/matches/${matchId}/approve/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { deliverableId, matchId }) => [
        { type: 'DeliverableMatches', id: deliverableId },
        { type: 'DeliverableMatch', id: matchId },
      ],
    }),

    // Reject deliverable match
    rejectDeliverableMatch: builder.mutation<
      DeliverableMatch,
      {
        matterId: string;
        taskId: string;
        deliverableId: string;
        matchId: string;
      }
    >({
      query: ({ matterId, taskId, deliverableId, matchId }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/deliverables/${deliverableId}/matches/${matchId}/reject/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { deliverableId, matchId }) => [
        { type: 'DeliverableMatches', id: deliverableId },
        { type: 'DeliverableMatch', id: matchId },
      ],
    }),

    // Get matches by artifact
    getMatchesByArtifact: builder.query<
      DeliverableMatch[],
      { matterId: string; taskId: string; artifactId: string }
    >({
      query: ({ matterId, taskId, artifactId }) =>
        `api/matters/${matterId}/tasks/${taskId}/deliverables/matches/by_artifact/?artifact_id=${artifactId}`,
      providesTags: (result, error, { artifactId }) => [
        { type: 'DeliverableMatches', id: `artifact-${artifactId}` },
      ],
    }),
  }),
});

export const {
  // Task deliverables
  useGetTaskDeliverablesQuery,
  useCreateTaskDeliverableMutation,
  useGetTaskDeliverableQuery,
  useUpdateTaskDeliverableMutation,
  usePartialUpdateTaskDeliverableMutation,
  useDeleteTaskDeliverableMutation,
  useBypassDeliverableMutation,
  useGetTaskDeliverableSummaryQuery,

  // Matter deliverables
  useGetMatterDeliverablesQuery,
  useGetMatterDeliverableQuery,
  useBatchCreateDeliverablesMutation,
  useGetPendingMatterDeliverablesQuery,
  useGetMatterDeliverableSummaryQuery,

  // Deliverable matches
  useGetDeliverableMatchesQuery,
  useCreateDeliverableMatchMutation,
  useGetDeliverableMatchQuery,
  useUpdateDeliverableMatchMutation,
  usePartialUpdateDeliverableMatchMutation,
  useDeleteDeliverableMatchMutation,
  useApproveDeliverableMatchMutation,
  useRejectDeliverableMatchMutation,
  useGetMatchesByArtifactQuery,
} = deliverablesApi;
